import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { IAccountStatementBE, IGetAccountStatementParams } from '@trader/api';
import { Progress } from '@trader/components';
import { useMst } from '@trader/store';
import { useI18next } from '@trader/services';
import { PDFDownloadLink, View } from '@react-pdf/renderer';

import { HtmlReport } from './report/htmlReport';
import { PDFReport } from './report/pdfReport';
import { defaultAccountState } from './constants/defaultState';
import { styles } from './styles';
import './report/styles.css';
import { recursivelyConvertJsonToPdfComponents } from './utils';

export interface IChartData {
  name: number;
  value: number;
}

export interface IAccountStatementDetails {
  from: string;
  to: string;
  platformLogin: string;
  currency: string;
  product: string;
  accountType: string;
}

export interface IAccountStatementAction {
  id: number;
  description: string;
}

export const AccountStatementReport: React.FC<IAccountStatementDetails> =
  observer(({ from, to, platformLogin, currency, product, accountType }) => {
    const store = useMst();
    const { translate } = useI18next();
    const [accountStatement, setAccountStatement] =
      useState<IAccountStatementBE>(defaultAccountState);
    const [isLoading, setIsLoading] = useState(true);
    const [pdfBalanceChart, setPdfBalanceChart] = useState(null);
    let timer;
    const timerInterval = 1000;

    const setChartData = () => {
      const chart = document.getElementById('balanceChart');

      if (chart) {
        clearInterval(timer);
        const chartHtml = chart.outerHTML
          .replace('<title></title>', '')
          .replace('<desc></desc>', '');

        const sv = recursivelyConvertJsonToPdfComponents(chartHtml, true);
        setPdfBalanceChart(sv);
      }
    };

    useEffect(() => {
      timer = setInterval(() => {
        setChartData();
      }, timerInterval);
    }, []);
    const Logo = () => {
      const logo = document.getElementById('logo');

      if (logo) {
        const logoHtml = logo.innerHTML;
        const sv = recursivelyConvertJsonToPdfComponents(logoHtml, false);

        return sv;
      }

      return <View></View>;
    };

    const getAccountStatement = async () => {
      const apiAccountStatementParams: IGetAccountStatementParams = {
        from: from,
        to: to,
        platformLogin: platformLogin,
      };

      const response = await store.pages.trading.getAccountStatementAsync.run(
        apiAccountStatementParams
      );

      if (response) {
        setAccountStatement(response);
      }
    };

    useEffect(() => {
      const fetchData = async () => {
        await getAccountStatement();
        setIsLoading(false);
      };

      fetchData();
    }, []);

    const PdfDownloadButton = () => {
      return (
        <PDFDownloadLink
          style={styles.confirmButton}
          document={
            <PDFReport
              logo={<Logo />}
              balanceChart={pdfBalanceChart}
              from={from}
              to={to}
              platformLogin={platformLogin}
              currency={currency}
              product={product}
              accountType={accountType}
              accountStatement={accountStatement}
            />
          }
          fileName='Account-Statement.pdf'
        >
          {({ loading }) =>
            loading
              ? translate('COMMON.LABELS.LOADING_DOCUMENT')
              : translate('COMMON.LABELS.DOWNLOAD')
          }
        </PDFDownloadLink>
      );
    };

    return isLoading ? (
      <Progress />
    ) : (
      <>
        <PdfDownloadButton />
        <HtmlReport
          from={from}
          to={to}
          platformLogin={platformLogin}
          currency={currency}
          product={product}
          accountType={accountType}
          accountStatement={accountStatement}
        />
      </>
    );
  });
